import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Users,
  CheckCircle,
  Clock,
  Check,
  ChevronDown,
  ChevronUp,
  FileText,
  Briefcase,
  Settings,
  AlertCircle,
  Play,
  UserCheck,
  Trash2,
  X,
  Download,
  Search,
  MapPin,
  Calendar,
  RotateCcw
} from 'lucide-react';
import PageHeader from './headers/Header';
import {
  getInProgressOnboardings,
  getStartWorkingOnboardings,
  getCompletedOnboardings,
  getOffices,
  updateOnboardingChecklist,
  deleteOnboarding
} from '../services/onboardingapi';

// Date handling
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExportButtons from './btns/ExportButtons';
import marabesLogo from '../assets/images/logos/Marabes.png';
import { Button, useTheme } from '@mui/material';

// ✅ Updated Helper: Format Date to DD/MM/YYYY
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Helper for PDF Image Loading
const getBase64FromUrl = async (url) => {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn("Could not load image for PDF:", url);
    return null;
  }
};

const fieldMapping = {
  identityDocument: 'copyOfIdentityDocumentProvided',
  diplomas: 'copyOfDiplomasProvided',
  criminalRecord: 'criminalRecordCertificateProvided',
  anapecCertificate: 'anapecCertificateProvided',
  rib: 'ribProvided',
  resume: 'resumeProvided',
  profilePicture: 'profilePictureProvided',
  cnss: 'cnssRegistered',
  internshipContract: 'internshipContractSigned',
  internalPolicy: 'internalPolicyDocumentSigned',
  officeFile: 'officeFilePrepared',
  officeTour: 'scheduleOfficeTourComplete',
  welcomePacket: 'welcomePacketComplete',
  trainingSession: 'introductoryTrainingSessionsComplete',
  initialProjects: 'initialProjectsAssigned',
  assignedTeam: 'assigningToSpecificTeamComplete',
  equipment: 'equipmentSetupComplete',
  workspace: 'workSpace',
  emailSetup: 'emailProfissional',
  appGroups: 'appGroupsAssigned'
};

const Onboarding = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [activeTable, setActiveTable] = useState('in-progress');
  const [activePhase, setActivePhase] = useState('phase1');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOffice, setSelectedOffice] = useState('all');
  const [onboardingRecords, setOnboardingRecords] = useState([]);
  const [originalRecords, setOriginalRecords] = useState([]);
  const [officesList, setOfficesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [credentialsForm, setCredentialsForm] = useState({
    recordId: null,
    email: '',
    password: '',
    confirmPassword: '',
    dob: '',
    gender: '',
    diploma: '',
    dateOfJoining: '',
    officeId: '',
    userName: '',
    company: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  // 🗓️ Date Filtering States
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const hasActiveFilters = Boolean(
    searchTerm ||
    selectedOffice !== 'all' ||
    selectedYear !== 'all' ||
    selectedMonth !== 'all' ||
    startDate ||
    endDate
  );

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedOffice('all');
    setSelectedYear('all');
    setSelectedMonth('all');
    setStartDate(null);
    setEndDate(null);
  };

  // 🔑 Scroll management refs
  const tableWrapperRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const isLocalUpdateRef = useRef(false);

  const phases = {
    phase1: {
      title: 'Phase 1: Pre-Arrival Preparation',
      icon: FileText,
      items: [
        { key: 'identityDocument', label: 'Copy of Identity Document', optional: false },
        { key: 'diplomas', label: 'Copy of Diplomas', optional: false },
        { key: 'criminalRecord', label: 'Criminal Record Certificate', optional: false },
        { key: 'anapecCertificate', label: 'ANAPEC Certificate', optional: false },
        { key: 'rib', label: 'RIB (Bank Account Details)', optional: false },
        { key: 'resume', label: 'Resume', optional: true },
        { key: 'profilePicture', label: 'Profile Picture', optional: false },
        { key: 'cnss', label: 'CNSS', optional: true },
        { key: 'internshipContract', label: 'Internship Contract', optional: false },
        { key: 'internalPolicy', label: 'Internal Policy Document', optional: false },
        { key: 'officeFile', label: 'Office File', optional: false },
        { key: 'officeTour', label: 'Schedule Office Tour', optional: false },
        { key: 'welcomePacket', label: 'Welcome Packet', optional: false }
      ]
    },
    phase2: {
      title: 'Phase 2: Onboarding at Academy',
      icon: Briefcase,
      items: [
        { key: 'trainingSession', label: 'Introductory Training Sessions', optional: false },
        { key: 'initialProjects', label: 'Initial Projects', optional: false },
        { key: 'assignedTeam', label: 'Assigning to a Specific Team', optional: false }
      ]
    },
    phase3: {
      title: 'Phase 3: Setup',
      icon: Settings,
      items: [
        { key: 'equipment', label: 'Equipment', optional: false },
        { key: 'workspace', label: 'Workspace', optional: false },
        { key: 'emailSetup', label: 'Email', optional: false },
        { key: 'appGroups', label: 'Add to App Groups', optional: false }
      ]
    }
  };

  // Helper functions
  const calculateOverallProgress = (record) => {
    const mandatoryItems = [
      ...phases.phase1.items.filter(item => !item.optional).map(item => record.phase1[item.key]),
      ...phases.phase2.items.filter(item => !item.optional).map(item => record.phase2[item.key]),
      ...phases.phase3.items.filter(item => !item.optional).map(item => record.phase3[item.key])
    ];
    const completed = mandatoryItems.filter(Boolean).length;
    return Math.round((completed / 18) * 100);
  };

  const transformToPhased = (item, status) => {
    const phase1 = {
      identityDocument: item.copyOfIdentityDocumentProvided || false,
      diplomas: item.copyOfDiplomasProvided || false,
      criminalRecord: item.criminalRecordCertificateProvided || false,
      anapecCertificate: item.anapecCertificateProvided || false,
      rib: item.ribProvided || false,
      resume: item.resumeProvided || false,
      profilePicture: item.profilePictureProvided || false,
      cnss: item.cnssRegistered || false,
      internshipContract: item.internshipContractSigned || false,
      internalPolicy: item.internalPolicyDocumentSigned || false,
      officeFile: item.officeFilePrepared || false,
      officeTour: item.scheduleOfficeTourComplete || false,
      welcomePacket: item.welcomePacketComplete || false
    };
    const phase2 = {
      trainingSession: item.introductoryTrainingSessionsComplete || false,
      initialProjects: item.initialProjectsAssigned || false,
      assignedTeam: item.assigningToSpecificTeamComplete || false
    };
    const phase3 = {
      equipment: item.equipmentSetupComplete || false,
      workspace: item.workSpace || false,
      emailSetup: item.emailProfissional || false,
      appGroups: item.appGroupsAssigned || false
    };

    let name = 'Unknown';
    let position = 'Unknown';

    if (item.employee) {
      if (item.employee.firstName && item.employee.lastName) {
        name = `${item.employee.firstName} ${item.employee.lastName}`.trim();
      }
      // ✅ Handle position array structure (like in Employees.js)
      if (Array.isArray(item.employee.positions) && item.employee.positions.length > 0) {
        position = item.employee.positions
          .map(p => p.positionName)
          .filter(Boolean)
          .join(', ') || 'Unknown';
      } else if (item.employee.position) {
        position = item.employee.position;
      }
    } else if (item.candidate) {
      if (item.candidate.firstName && item.candidate.lastName) {
        name = `${item.candidate.firstName} ${item.candidate.lastName}`.trim();
      }
      if (item.candidate.position) {
        position = item.candidate.position;
      }
    }

    // Default office from DB
    let officeName = item.office?.name || item.candidate?.office || 'Unknown';

    // ✅ Force Override for Mohammed Bounasr (Business Logic)
    if (name.toLowerCase().trim() === 'mohammed bounasr') {
      officeName = 'Tangier';
    }

    return {
      id: item.onboardingId,
      name: name,
      position: position,
      office: officeName,
      officeId: item.office?.id || '',
      status: status,
      phase1,
      phase2,
      phase3,
      credentials: item.email ? { email: item.email, userName: item.userName } : undefined,
      dob: item.dob ? formatDate(item.dob) : '', // DD/MM/YYYY
      gender: item.gender || '',
      diploma: item.diploma || '',
      dateOfJoining: item.dateOfJoining ? formatDate(item.dateOfJoining) : '', // DD/MM/YYYY
      userName: item.userName || '',
      sw: status === 'started-working'
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [inProgressRes, startWorkingRes, completedRes, officesRes] = await Promise.all([
          getInProgressOnboardings(),
          getStartWorkingOnboardings(),
          getCompletedOnboardings(),
          getOffices()
        ]);

        const getOfficeId = (item, offices) => {
          if (item.office?.id) {
            return item.office.id;
          }
          const candidateOfficeName = item.candidate?.office;
          if (candidateOfficeName) {
            const matchingOffice = offices.find(o => o.name === candidateOfficeName);
            return matchingOffice ? matchingOffice.id : '';
          }
          return '';
        };

        const inProgress = inProgressRes.map(item => ({
          ...transformToPhased(item, 'in-progress'),
          officeId: getOfficeId(item, officesRes)
        }));
        const startWorking = startWorkingRes.map(item => ({
          ...transformToPhased(item, 'started-working'),
          officeId: getOfficeId(item, officesRes)
        }));
        const completed = completedRes.map(item => ({
          ...transformToPhased(item, 'completed'),
          officeId: getOfficeId(item, officesRes)
        }));
        const allRecords = [...inProgress, ...startWorking, ...completed];
        setOnboardingRecords(allRecords);
        setOriginalRecords(JSON.parse(JSON.stringify(allRecords)));
        setOfficesList([{ id: 'all', name: 'All Offices' }, ...officesRes]);
      } catch (err) {
        console.error('Failed to fetch onboarding data:', err);
        setError('Failed to load onboarding records.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleCheckboxChange = (recordId, phase, itemKey) => {
    if (tableWrapperRef.current) {
      scrollPositionRef.current = tableWrapperRef.current.scrollLeft;
    }
    isLocalUpdateRef.current = true;
    setOnboardingRecords(prev =>
      prev.map(r =>
        r.id === recordId
          ? { ...r, [phase]: { ...r[phase], [itemKey]: !r[phase][itemKey] } }
          : r
      )
    );
  };

  const handleSWChange = (recordId, checked) => {
    if (tableWrapperRef.current) {
      scrollPositionRef.current = tableWrapperRef.current.scrollLeft;
    }
    isLocalUpdateRef.current = true;
    if (checked) {
      const record = onboardingRecords.find(r => r.id === recordId);

      let prefilledOfficeId = record.officeId || '';

      if (record.name.toLowerCase().trim() === 'mohammed bounasr') {
        const tangierOffice = officesList.find(o => o.name.toLowerCase() === 'tangier');
        if (tangierOffice) prefilledOfficeId = tangierOffice.id;
      }

      setCredentialsForm({
        recordId,
        email: record.credentials?.email || '',
        password: '',
        confirmPassword: '',
        dob: record.dob || '',
        gender: record.gender || '',
        diploma: record.diploma || '',
        dateOfJoining: record.dateOfJoining || '',
        officeId: prefilledOfficeId,
        userName: record.userName || ''
      });
      setShowCredentialsModal(true);
    } else {
      setOnboardingRecords(prev =>
        prev.map(r => (r.id === recordId ? { ...r, sw: false, status: 'in-progress' } : r))
      );
    }
  };

  const handleDelete = async (recordId, recordName) => {
    if (!window.confirm(`Are you sure you want to delete the onboarding record for ${recordName}?`)) {
      return;
    }

    setIsSaving(true);
    try {
      await deleteOnboarding(recordId);
      setOnboardingRecords(prev => prev.filter(r => r.id !== recordId));
      setOriginalRecords(prev => prev.filter(r => r.id !== recordId));
    } catch (err) {
      console.error('Failed to delete onboarding:', err);
      setError('Failed to delete onboarding record.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCredentialsSubmit = async () => {
    const requiredFields = ['email', 'password', 'dob', 'gender', 'diploma', 'dateOfJoining', 'officeId', 'userName', 'company'];
    let hasError = false;
    for (const field of requiredFields) {
      if (!credentialsForm[field]) {
        console.warn(`Missing required field: ${field}`);
        hasError = true;
      }
    }
    if (credentialsForm.password !== credentialsForm.confirmPassword) {
      console.warn('Passwords do not match');
      hasError = true;
    }
    if (hasError) return;

    setIsSaving(true);
    try {
      const record = onboardingRecords.find(r => r.id === credentialsForm.recordId);
      const checklistUpdates = {};
      Object.keys(record.phase1).forEach(key => {
        checklistUpdates[fieldMapping[key]] = record.phase1[key];
      });
      Object.keys(record.phase2).forEach(key => {
        checklistUpdates[fieldMapping[key]] = record.phase2[key];
      });
      Object.keys(record.phase3).forEach(key => {
        checklistUpdates[fieldMapping[key]] = record.phase3[key];
      });

      let finalOfficeId = credentialsForm.officeId;
      if (record && record.name.toLowerCase().trim() === 'mohammed bounasr') {
        const tangierOffice = officesList.find(o => o.name.toLowerCase() === 'tangier');
        if (tangierOffice) {
          finalOfficeId = tangierOffice.id;
        }
      }

      const payload = {
        checklistUpdates,
        email: credentialsForm.email,
        password: credentialsForm.password,
        dob: credentialsForm.dob,
        gender: credentialsForm.gender,
        diploma: credentialsForm.diploma,
        dateOfJoining: credentialsForm.dateOfJoining,
        officeId: finalOfficeId,
        userName: credentialsForm.userName,
        company: credentialsForm.company,
        forceStartWorking: true
      };

      const updatedRecord = await updateOnboardingChecklist(credentialsForm.recordId, payload);
      setOnboardingRecords(prev =>
        prev.map(r =>
          r.id === credentialsForm.recordId
            ? transformToPhased(updatedRecord, updatedRecord.status.toLowerCase().replace('_', '-'))
            : r
        )
      );
      setOriginalRecords(prev =>
        prev.map(r =>
          r.id === credentialsForm.recordId
            ? transformToPhased(updatedRecord, updatedRecord.status.toLowerCase().replace('_', '-'))
            : r
        )
      );
      setShowCredentialsModal(false);
      setCredentialsForm({
        recordId: null,
        email: '',
        password: '',
        confirmPassword: '',
        dob: '',
        gender: '',
        diploma: '',
        dateOfJoining: '',
        officeId: '',
        userName: '',
        company: '',
      });
    } catch (err) {
      console.error('Failed to save credentials:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const hasUnsavedChanges = useCallback(() => {
    return onboardingRecords.some((record, idx) => {
      const original = originalRecords[idx];
      if (!original) return false;
      return (
        JSON.stringify(record.phase1) !== JSON.stringify(original.phase1) ||
        JSON.stringify(record.phase2) !== JSON.stringify(original.phase2) ||
        JSON.stringify(record.phase3) !== JSON.stringify(original.phase3) ||
        record.sw !== original.sw
      );
    });
  }, [onboardingRecords, originalRecords]);

  const handleSaveChanges = async () => {
    setIsSaving(true);
    try {
      const promises = onboardingRecords.map(async (record, idx) => {
        const original = originalRecords[idx];
        if (!original) return;
        const checklistUpdates = {};
        Object.keys(record.phase1).forEach(key => {
          checklistUpdates[fieldMapping[key]] = record.phase1[key];
        });
        Object.keys(record.phase2).forEach(key => {
          checklistUpdates[fieldMapping[key]] = record.phase2[key];
        });
        Object.keys(record.phase3).forEach(key => {
          checklistUpdates[fieldMapping[key]] = record.phase3[key];
        });

        let finalOfficeId = record.officeId;
        if (record.name.toLowerCase().trim() === 'mohammed bounasr') {
          const tangierOffice = officesList.find(o => o.name.toLowerCase() === 'tangier');
          if (tangierOffice) {
            finalOfficeId = tangierOffice.id;
          }
        }

        if (Object.keys(checklistUpdates).length > 0) {
          await updateOnboardingChecklist(record.id, {
            checklistUpdates,
            officeId: finalOfficeId
          });
        }
      });
      await Promise.all(promises);
      const [inProgressRes, startWorkingRes, completedRes] = await Promise.all([
        getInProgressOnboardings(),
        getStartWorkingOnboardings(),
        getCompletedOnboardings()
      ]);
      const inProgress = inProgressRes.map(item => transformToPhased(item, 'in-progress'));
      const startWorking = startWorkingRes.map(item => transformToPhased(item, 'started-working'));
      const completed = completedRes.map(item => transformToPhased(item, 'completed'));
      const allRecords = [...inProgress, ...startWorking, ...completed];

      isLocalUpdateRef.current = false;

      setOnboardingRecords(allRecords);
      setOriginalRecords(JSON.parse(JSON.stringify(allRecords)));
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (tableWrapperRef.current && isLocalUpdateRef.current) {
      requestAnimationFrame(() => {
        tableWrapperRef.current.scrollLeft = scrollPositionRef.current;
        isLocalUpdateRef.current = false;
      });
    }
  }, [onboardingRecords]);

  // 🔍 Filtering Logic
  const getFilteredRecords = useCallback((status) => {
    return onboardingRecords.filter(r => {
      const matchesSearch = r.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesOffice = selectedOffice === 'all' || r.officeId === selectedOffice;
      const matchesStatus = r.status === status;

      let matchesDate = true;
      if (r.dateOfJoining) {
        // Re-parsing DD/MM/YYYY to Date object
        const parts = r.dateOfJoining.split('/');
        if (parts.length === 3) {
          const recordDate = new Date(parts[2], parts[1] - 1, parts[0]); // YYYY, MM-1, DD

          if (selectedYear !== 'all') {
            if (recordDate.getFullYear() !== parseInt(selectedYear)) {
              matchesDate = false;
            }
          }

          if (selectedMonth !== 'all') {
            if (recordDate.getMonth() !== parseInt(selectedMonth)) {
              matchesDate = false;
            }
          }

          if (startDate && endDate) {
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);

            if (recordDate < start || recordDate > end) {
              matchesDate = false;
            }
          }
        }
      }

      return matchesStatus && matchesSearch && matchesOffice && matchesDate;
    });
  }, [onboardingRecords, searchTerm, selectedOffice, selectedYear, selectedMonth, startDate, endDate]);

  const filteredInProgress = useMemo(() => getFilteredRecords('in-progress'), [getFilteredRecords]);
  const filteredStarted = useMemo(() => getFilteredRecords('started-working'), [getFilteredRecords]);
  const filteredCompleted = useMemo(() => getFilteredRecords('completed'), [getFilteredRecords]);

  // 📄 PDF Export Function - (Simplified for brevity, ensuring imports work)
  const exportPDF = async () => {
    setExportingPdf(true);
    try {
      const doc = new jsPDF('l', 'mm', 'a4'); // Landscape
      const pageWidth = doc.internal.pageSize.width;
      const margin = 10;
      const contentWidth = pageWidth - (margin * 2);

      const axoLogoUrl = '/cLogo.png';
      const axoBase64 = await getBase64FromUrl(axoLogoUrl);

      // Left Logo
      try {
        doc.addImage(marabesLogo, 'PNG', margin, 10, 35, 12);
      } catch (e) {
        console.warn("Marabes logo load failed", e);
      }

      // Right Logo
      if (axoBase64) {
        doc.addImage(axoBase64, 'PNG', pageWidth - margin - 35, 10, 35, 12);
      }

      // Divider
      doc.setDrawColor(63, 81, 181);
      doc.setLineWidth(0.5);
      doc.line(margin, 25, pageWidth - margin, 25);

      // Header Bar
      doc.setFillColor(232, 245, 255);
      doc.rect(margin, 30, contentWidth, 14, 'F');

      const textY = 39;

      // Date
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      doc.text(`Date: ${new Date().toLocaleDateString('en-GB')}`, margin + 3, textY);

      // Centered Title
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(40, 58, 139);
      doc.text('Onboarding Report', pageWidth / 2, textY, { align: 'center' });

      // Total Records
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      const recordsToPrint = [...filteredInProgress, ...filteredStarted, ...filteredCompleted];
      const recordsText = `Total Records: ${recordsToPrint.length}`;
      const textWidth = doc.getTextWidth(recordsText);
      doc.text(recordsText, margin + contentWidth - 3 - textWidth, textY);

      const tableRows = recordsToPrint.map(r => [
        r.name,
        r.position || 'Unknown',
        r.office,
        r.dateOfJoining || 'Start Date Pending',
        // ✅ Format Status for Export
        r.status === 'completed' ? 'Completed' : r.status.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
      ]);

      autoTable(doc, {
        startY: 45,
        head: [['Name', 'Position', 'Office', 'Start Date', 'Status']],
        body: tableRows,
        theme: 'grid',
        headStyles: {
          fillColor: [63, 81, 181],
          textColor: 255,
          fontSize: 10,
          fontStyle: 'bold'
        },
        styles: {
          fontSize: 9,
          cellPadding: 3
        },
        margin: { left: margin, right: margin }
      });

      doc.save(`onboarding-report-${new Date().toISOString().split('T')[0]}.pdf`);

    } catch (err) {
      console.error("PDF Export Error", err);
      alert("Failed to export PDF");
    } finally {
      setExportingPdf(false);
    }
  };

  // ✅ CSS Styles for Input Sizing (Height: 40px)
  const inputStyle = {
    height: '40px',
    borderRadius: '4px',
    border: `1px solid ${isDark ? '#4b5563' : '#c4c4c4'}`,
    padding: '0 12px',
    boxSizing: 'border-box',
    fontSize: '14px',
    width: '100%',
    minWidth: '120px',
    backgroundColor: isDark ? '#1e293b' : 'white',
    color: isDark ? '#e2e8f0' : '#1e293b',
    outline: 'none'
  };

  const PhaseTab = ({ phase, title, icon: Icon, isActive, onClick }) => (
    <button
      onClick={onClick}
      className={`employees__tab-btn ${isActive ? 'active' : ''}`}
      style={{ padding: '1rem 1.5rem' }}
    >
      <Icon size={18} style={{ marginRight: '0.5rem' }} />
      {title}
    </button>
  );

  const renderTableContent = (records, status) => {
    const currentPhase = phases[activePhase];
    return (
      <>
        <div className="employees__table-wrapper" ref={tableWrapperRef}>
          <table className="employees__table">
            <thead>
              <tr className="employees__table-header-row">
                <th className="employees__table-header">Name</th>
                {currentPhase.items.map(item => (
                  <th key={item.key} className="employees__table-header">
                    {item.label}
                    {item.optional && (
                      <span
                        style={{
                          fontSize: '0.75rem',
                          color: 'var(--accent-color)',
                          marginLeft: '0.5rem',
                          fontWeight: '600'
                        }}
                      >
                        (Optional)
                      </span>
                    )}
                  </th>
                ))}
                <th className="employees__table-header">Overall Progress</th>
                {status === 'in-progress' && <th className="employees__table-header">SW</th>}
                <th className="employees__table-header">Actions</th>
              </tr>
            </thead>
            <tbody>
              {records.map(record => (
                <tr key={record.id} className="employees__table-row">
                  <td className="employees__table-cell">{record.name}</td>
                  {currentPhase.items.map(item => (
                    <td key={item.key} className="employees__table-cell" style={{ textAlign: 'center' }}>
                      <label className="checkbox-wrapper" style={{ display: 'inline-flex', cursor: 'pointer', position: 'relative' }}>
                        <input
                          type="checkbox"
                          checked={record[activePhase][item.key] || false}
                          onChange={() => handleCheckboxChange(record.id, activePhase, item.key)}
                          style={{
                            position: 'absolute',
                            opacity: 0,
                            width: '1px',
                            height: '1px',
                            overflow: 'hidden',
                            pointerEvents: 'none'
                          }}
                        />
                        <span
                          className="checkmark"
                          style={{
                            width: '26px',
                            height: '26px',
                            border: '2px solid var(--border-color)',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: record[activePhase][item.key] ? 'var(--success-color)' : 'var(--white)',
                            borderColor: record[activePhase][item.key] ? 'var(--success-color)' : 'var(--border-color)',
                            color: 'var(--white)',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          {record[activePhase][item.key] && <Check size={14} />}
                        </span>
                      </label>
                    </td>
                  ))}
                  <td className="employees__table-cell">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
                      <div
                        style={{
                          flex: 1,
                          height: '10px',
                          backgroundColor: 'var(--border-color)',
                          borderRadius: '8px',
                          overflow: 'hidden'
                        }}
                      >
                        <div
                          style={{
                            height: '100%',
                            width: `${calculateOverallProgress(record)}%`,
                            backgroundColor: 'var(--success-color)',
                            borderRadius: '8px',
                            transition: 'width 0.4s ease'
                          }}
                        />
                      </div>
                      <span
                        style={{
                          fontWeight: '600',
                          color: 'var(--success-color)',
                          minWidth: '50px'
                        }}
                      >
                        {calculateOverallProgress(record)}%
                      </span>
                    </div>
                  </td>
                  {status === 'in-progress' && (
                    <td className="employees__table-cell" style={{ textAlign: 'center' }}>
                      <label className="checkbox-wrapper" style={{ display: 'inline-flex', cursor: 'pointer', position: 'relative' }}>
                        <input
                          type="checkbox"
                          checked={record.sw || false}
                          onChange={e => handleSWChange(record.id, e.target.checked)}
                          style={{
                            position: 'absolute',
                            opacity: 0,
                            width: '1px',
                            height: '1px',
                            overflow: 'hidden',
                            pointerEvents: 'none'
                          }}
                        />
                        <span
                          className="checkmark"
                          style={{
                            width: '26px',
                            height: '26px',
                            border: '2px solid var(--border-color)',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: record.sw ? 'var(--primary-color)' : 'var(--white)',
                            borderColor: record.sw ? 'var(--primary-color)' : 'var(--border-color)',
                            color: 'var(--white)',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          {record.sw && <Check size={14} />}
                        </span>
                      </label>
                    </td>
                  )}
                  <td className="employees__table-cell" style={{ textAlign: 'center' }}>
                    <button
                      onClick={() => handleDelete(record.id, record.name)}
                      className="btn btn-danger"
                      style={{
                        padding: '0.5rem',
                        background: 'var(--danger-color, #ef4444)',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer'
                      }}
                      title="Delete Onboarding"
                    >
                      <Trash2 size={18} color="#fff" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {activeTable === status && hasUnsavedChanges() && (
          <div className="employees__pagination">
            <div></div>
            <button
              onClick={handleSaveChanges}
              disabled={isSaving}
              className="btn btn-primary"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem'
              }}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </>
    );
  };

  const TableSection = ({ title, status, icon: Icon, lightBgColor, darkBgColor }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const records = status === 'in-progress' ? filteredInProgress :
      status === 'started-working' ? filteredStarted :
        filteredCompleted;
    const isActive = activeTable === status;
    const headerBg = isDark ? darkBgColor : lightBgColor;
    const headerColor = isDark ? '#e2e8f0' : '#1e293b';

    return (
      <div className="employees__table-card" style={{ marginBottom: '1.5rem' }}>
        <button
          className="employees__table-header"
          onClick={() => setActiveTable(status)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            padding: '1.5rem 2rem',
            background: headerBg,
            color: headerColor,
            border: 'none',
            cursor: 'pointer',
            borderRadius: isActive ? '16px 16px 0 0' : '16px',
            transition: 'border-radius 0.3s ease'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <Icon size={20} />
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700' }}>{title}</h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <span
              style={{
                background: 'rgba(0,0,0,0.05)',
                padding: '0.375rem 0.875rem',
                borderRadius: '9999px',
                fontSize: '0.875rem'
              }}
            >
              {records.length}
            </span>
            {isActive ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
        </button>
        {isActive && (
          <div>
            <div className="employees__tabs">
              <PhaseTab
                phase="phase1"
                title="Pre-Arrival"
                icon={phases.phase1.icon}
                isActive={activePhase === 'phase1'}
                onClick={() => setActivePhase('phase1')}
              />
              <PhaseTab
                phase="phase2"
                title="Academy"
                icon={phases.phase2.icon}
                isActive={activePhase === 'phase2'}
                onClick={() => setActivePhase('phase2')}
              />
              <PhaseTab
                phase="phase3"
                title="Setup"
                icon={phases.phase3.icon}
                isActive={activePhase === 'phase3'}
                onClick={() => setActivePhase('phase3')}
              />
            </div>
            {records.length > 0 ? (
              renderTableContent(records, status)
            ) : (
              <div
                style={{
                  padding: '3rem 2rem',
                  textAlign: 'center',
                  color: 'var(--text-muted)',
                  borderBottomLeftRadius: '16px',
                  borderBottomRightRadius: '16px'
                }}
              >
                <Icon size={48} style={{ marginBottom: '1rem', color: '#cbd5e1' }} />
                <p>No records found</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="employees__container" style={{ padding: '3rem', display: 'flex', justifyContent: 'center' }}>
        <div className="employees__table-loading">
          <div style={{ fontSize: '1.25rem', color: 'var(--text-muted)' }}>Loading onboarding data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="employees__container" style={{ padding: '2rem' }}>
        <div className="employees__message error" style={{ position: 'relative', top: 0, right: 0, margin: '0 auto' }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div className="employees__container">
        {/* HEADER */}
        <PageHeader
          title="Onboarding Dashboard"
          subtitle="Manage new hires, track progress, and ensure a smooth onboarding experience."
        />
        <div className="employees__table-section">
          <div className="employees__table-card">
            {/*  <div className="employees__table-header-container">
              <h1 className="employees__table-title">
                <Users size={32} style={{ marginRight: '0.75rem' }} />
                Employee Onboarding Management
              </h1>
            </div> */}

            {/* ✅ FLATTENED FILTER CONTAINER (Single Line) */}
            <div style={{ padding: '16px', display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>

              {/* Search */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-dark)' }}>Search</label>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <Search
                    size={16}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      color: '#1976d2',
                      pointerEvents: 'none',
                      zIndex: 1,
                      flexShrink: 0
                    }}
                  />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    style={{ ...inputStyle, paddingLeft: '36px' }}
                    className="axo-search-input"
                  />
                </div>
              </div>

              {/* Office */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-dark)' }}>Office</label>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <MapPin
                    size={16}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      color: '#1976d2',
                      pointerEvents: 'none',
                      zIndex: 1,
                      flexShrink: 0
                    }}
                  />
                  <select
                    value={selectedOffice}
                    onChange={e => setSelectedOffice(e.target.value)}
                    className="axo-filter-select"
                    style={{
                      paddingLeft: '38px',
                      borderColor: selectedOffice !== 'all' ? '#1976d2' : undefined,
                      fontWeight: selectedOffice !== 'all' ? 600 : undefined
                    }}
                  >
                    {officesList.map(office => (
                      <option key={office.id} value={office.id}>{office.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Year */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-dark)' }}>Year</label>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <Calendar
                    size={16}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      color: '#1976d2',
                      pointerEvents: 'none',
                      zIndex: 1,
                      flexShrink: 0
                    }}
                  />
                  <select
                    value={selectedYear}
                    onChange={e => setSelectedYear(e.target.value)}
                    className="axo-filter-select"
                    style={{
                      paddingLeft: '38px',
                      borderColor: selectedYear !== 'all' ? '#1976d2' : undefined,
                      fontWeight: selectedYear !== 'all' ? 600 : undefined
                    }}
                  >
                    <option value="all">All</option>
                    <option value="2025">2025</option>
                    <option value="2026">2026</option>
                  </select>
                </div>
              </div>

              {/* Month */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-dark)' }}>Month</label>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <Calendar
                    size={16}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      color: '#1976d2',
                      pointerEvents: 'none',
                      zIndex: 1,
                      flexShrink: 0
                    }}
                  />
                  <select
                    value={selectedMonth}
                    onChange={e => setSelectedMonth(e.target.value)}
                    className="axo-filter-select"
                    style={{
                      paddingLeft: '38px',
                      borderColor: selectedMonth !== 'all' ? '#1976d2' : undefined,
                      fontWeight: selectedMonth !== 'all' ? 600 : undefined
                    }}
                  >
                    <option value="all">All</option>
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i} value={i}>{new Date(0, i).toLocaleString('default', { month: 'short' })}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Start Date */}
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    size: 'small',
                    style: { width: 180, height: '40px' },
                    InputProps: { style: { height: '40px', borderRadius: '4px', fontSize: '14px' }, className: "axo-search-input" },
                    InputLabelProps: { style: { fontSize: '14px' } }
                  }
                }}
              />

              {/* End Date */}
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    size: 'small',
                    style: { width: 180, height: '40px' },
                    InputProps: { style: { height: '40px', borderRadius: '4px', fontSize: '14px' }, className: "axo-search-input" },
                    InputLabelProps: { style: { fontSize: '14px' } }
                  }
                }}
              />

              {/* Reset Button */}
              {hasActiveFilters && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<RotateCcw size={16} />}
                  onClick={resetFilters}
                  sx={{ height: '40px', borderRadius: '4px', textTransform: 'none', fontWeight: 600 }}
                >
                  Reset Filters
                </Button>
              )}

              {/* Export Button */}
              <ExportButtons
                onExportPDF={exportPDF}
                isExporting={exportingPdf}
              />

            </div>
          </div>
        </div>

        <TableSection
          title="Onboardings in Progress"
          status="in-progress"
          icon={Clock}
          lightBgColor="#e0f2fe"
          darkBgColor="#0c2840"
        />
        <TableSection
          title="Started Working"
          status="started-working"
          icon={Play}
          lightBgColor="#dbeafe"
          darkBgColor="#1e3a5f"
        />
        <TableSection
          title="Completed Onboardings"
          status="completed"
          icon={CheckCircle}
          lightBgColor="#dcfce7"
          darkBgColor="#052e16"
        />

        {showCredentialsModal && (
          <div
            className="form-overlay"
            onClick={e => {
              if (e.target === e.currentTarget) {
                setShowCredentialsModal(false);
                setCredentialsForm({
                  recordId: null,
                  email: '',
                  password: '',
                  confirmPassword: '',
                  dob: '',
                  gender: '',
                  diploma: '',
                  dateOfJoining: '',
                  officeId: '',
                  userName: '',
                  company: ''
                });
              }
            }}
          >
            <div className="form-modal" style={{ maxWidth: '600px', width: '90%' }}>
              <button className="close-btn" onClick={() => setShowCredentialsModal(false)}>
                <X size={24} />
              </button>
              <h2 style={{ display: 'flex', alignItems: 'center', marginBottom: '1.5rem', color: 'var(--text-dark)' }}>
                <UserCheck size={24} style={{ marginRight: '0.5rem', color: 'var(--primary-color)' }} />
                Create Employee Credentials
              </h2>
              <div
                className="alert-box"
                style={{
                  background: 'var(--warning-light, #fef3c7)',
                  border: '1px solid var(--warning-main, #f59e0b)',
                  padding: '1rem',
                  borderRadius: '8px',
                  display: 'flex',
                  gap: '0.75rem',
                  alignItems: 'flex-start',
                  marginBottom: '1.5rem'
                }}
              >
                <AlertCircle size={20} style={{ color: 'var(--warning-dark, #d97706)', marginTop: '0.25rem', flexShrink: 0 }} />
                <div style={{ color: 'var(--text-dark)' }}>
                  <strong>Important:</strong> This candidate will become an employee and move to "Started Working" status.
                </div>
              </div>
              <form
                onSubmit={e => {
                  e.preventDefault();
                  handleCredentialsSubmit();
                }}
              >
                <div className="form-row" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                  <div className="form-group half-width" style={{ flex: '1 1 45%' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-dark)' }}>Email *</label>
                    <input
                      type="email"
                      value={credentialsForm.email}
                      onChange={e => setCredentialsForm(prev => ({ ...prev, email: e.target.value }))}
                      required
                      placeholder="employee@company.com"
                      className="axo-search-input"
                      style={{ paddingLeft: '1rem', width: '100%', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div className="form-group half-width" style={{ flex: '1 1 45%' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-dark)' }}>Username *</label>
                    <input
                      type="text"
                      value={credentialsForm.userName}
                      onChange={e => setCredentialsForm(prev => ({ ...prev, userName: e.target.value }))}
                      required
                      placeholder="employee.username"
                      className="axo-search-input"
                      style={{ paddingLeft: '1rem', width: '100%', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>

                <div className="form-row" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                  <div className="form-group half-width" style={{ flex: '1 1 45%' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-dark)' }}>Password *</label>
                    <input
                      type="password"
                      value={credentialsForm.password}
                      onChange={e => setCredentialsForm(prev => ({ ...prev, password: e.target.value }))}
                      required
                      placeholder="••••••••"
                      className="axo-search-input"
                      style={{ paddingLeft: '1rem', width: '100%', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div className="form-group half-width" style={{ flex: '1 1 45%' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-dark)' }}>Confirm Password *</label>
                    <input
                      type="password"
                      value={credentialsForm.confirmPassword}
                      onChange={e => setCredentialsForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      required
                      placeholder="••••••••"
                      className="axo-search-input"
                      style={{ paddingLeft: '1rem', width: '100%', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>

                <div className="form-row" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                  <div className="form-group half-width" style={{ flex: '1 1 45%' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-dark)' }}>Date of Birth *</label>
                    <input
                      type="date"
                      value={credentialsForm.dob}
                      onChange={e => setCredentialsForm(prev => ({ ...prev, dob: e.target.value }))}
                      required
                      className="axo-search-input"
                      style={{ paddingLeft: '1rem', width: '100%', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div className="form-group half-width" style={{ flex: '1 1 45%' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-dark)' }}>Gender *</label>
                    <select
                      value={credentialsForm.gender}
                      onChange={e => setCredentialsForm(prev => ({ ...prev, gender: e.target.value }))}
                      required
                      className="axo-filter-select"
                      style={{ paddingRight: '2.5rem', width: '100%', boxSizing: 'border-box' }}
                    >
                      <option value="">Select</option>
                      <option value="MALE">Male</option>
                      <option value="FEMALE">Female</option>
                    </select>
                  </div>
                </div>

                <div className="form-row" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                  <div className="form-group half-width" style={{ flex: '1 1 45%' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-dark)' }}>Diploma *</label>
                    <select
                      value={credentialsForm.diploma}
                      onChange={e => setCredentialsForm(prev => ({ ...prev, diploma: e.target.value }))}
                      required
                      className="axo-filter-select"
                      style={{ paddingRight: '2.5rem', width: '100%', boxSizing: 'border-box' }}
                    >
                      <option value="">Select</option>
                      <option value="High School">High School</option>
                      <option value="Technician">Technician</option>
                      <option value="Bachelor">Bachelor</option>
                      <option value="Engineer">Engineer</option>
                      <option value="Master">Master</option>
                      <option value="PhD">PhD</option>
                    </select>
                  </div>
                  <div className="form-group half-width" style={{ flex: '1 1 45%' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-dark)' }}>Start Date *</label>
                    <input
                      type="date"
                      value={credentialsForm.dateOfJoining}
                      onChange={e => setCredentialsForm(prev => ({ ...prev, dateOfJoining: e.target.value }))}
                      required
                      className="axo-search-input"
                      style={{ paddingLeft: '1rem', width: '100%', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>

                <div className="form-row" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                  <div className="form-group half-width" style={{ flex: '1 1 45%' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-dark)' }}>Office *</label>
                    <select
                      value={credentialsForm.officeId}
                      onChange={e => setCredentialsForm(prev => ({ ...prev, officeId: e.target.value }))}
                      required
                      className="axo-filter-select"
                      style={{ paddingRight: '2.5rem', width: '100%', boxSizing: 'border-box' }}
                    >
                      <option value="">Select</option>
                      {officesList
                        .filter(office => office.id !== 'all')
                        .map(office => (
                          <option key={office.id} value={office.id}>
                            {office.name}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="form-group half-width" style={{ flex: '1 1 45%' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-dark)' }}>Company *</label>
                    <select
                      value={credentialsForm.company}
                      onChange={e => setCredentialsForm(prev => ({ ...prev, company: e.target.value }))}
                      required
                      className="axo-filter-select"
                      style={{ paddingRight: '2.5rem', width: '100%', boxSizing: 'border-box' }}
                    >
                      <option value="">Select Company</option>
                      <option value="MG">MG</option>
                      <option value="MC">MC</option>
                    </select>
                  </div>
                </div>

                <div className="form-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '2rem' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCredentialsModal(false);
                      setCredentialsForm({
                        recordId: null,
                        email: '',
                        password: '',
                        confirmPassword: '',
                        dob: '',
                        gender: '',
                        diploma: '',
                        dateOfJoining: '',
                        officeId: '',
                        userName: '',
                        company: ''
                      });
                    }}
                    style={{
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      border: '1px solid var(--border-color, #cbd5e1)',
                      background: 'transparent',
                      color: 'var(--text-dark)',
                      cursor: 'pointer',
                      fontWeight: 600
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    style={{
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      border: 'none',
                      background: 'var(--primary-color, #2563eb)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      cursor: isSaving ? 'not-allowed' : 'pointer',
                      opacity: isSaving ? 0.7 : 1,
                      fontWeight: 600
                    }}
                  >
                    <Check size={18} style={{ marginRight: '0.5rem' }} />
                    {isSaving ? 'Saving...' : 'Create & Move to Started Working'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </LocalizationProvider>
  );
};

export default Onboarding;
